/**
 * S-04 Signup — account (V1 auth-profiles-gdpr). Email+password (strength
 * meter), or magic-link / Google shortcut. Requires the S-02 DOB (redirects
 * back if missing — the gate is FIRST, screens.md).
 *
 * Signup is always 202 (no enumeration). Right after it, the screen attempts
 * one login: the e2e cohort (auto-verified) and pre-verified paths land a
 * session and route by lifecycle; everyone else gets 403 EMAIL_UNVERIFIED and
 * goes to the S-05 pending screen. A taken email also lands here (the server
 * mailed an "account exists" sign-in link instead — stated honestly).
 */
import { useMemo, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { copy, resolveCopy } from "@humble/shared";
import { Button, Card, IconBrokenHeart, toast } from "../../design/index.js";
import { api, getDeviceFp, isApiError } from "../../lib/api.js";
import { setSession, useStore } from "../../lib/store.js";
import { estimateStrength, isDisposableDomain } from "./password-strength.js";
import {
  browserTz,
  routeForLifecycle,
  setSignupAccount,
  signupFlow,
  writeUnderageLockRef,
} from "./signup-flow.js";
import { startGoogleSignIn } from "./google-pkce.js";
import { UnderageLock } from "./underage-lock.js";
import "./auth.css";

export function SignupAccountScreen() {
  const dob = useStore(signupFlow, (s) => s.dob);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicSent, setMagicSent] = useState(false);
  const [lockRef, setLockRef] = useState<string | null>(null);

  const strength = useMemo(() => estimateStrength(password), [password]);
  const disposable = useMemo(() => isDisposableDomain(email), [email]);

  if (lockRef) return <UnderageLock lockRef={lockRef} />;
  // DOB gate is the FIRST step — no account form without it (S-02 order).
  if (!dob) return <Navigate to="/signup/age" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!strength.meetsMinimum) {
      setError("Password too short — 10 characters minimum.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/signup", {
        body: { email, password, dob: dob!, device_fp: getDeviceFp(), tz: browserTz() },
      });
      setSignupAccount(email, password);
      // One login attempt: verified/e2e accounts get a session now; everyone
      // else is EMAIL_UNVERIFIED and heads to the S-05 pending screen.
      try {
        const pair = await api.post("/auth/login", {
          body: { email, password, device_fp: getDeviceFp() },
        });
        if (pair?.access_token) {
          setSession(pair.access_token, pair.user ?? null, pair.lifecycle ?? null);
          navigate(routeForLifecycle(pair.lifecycle));
          return;
        }
      } catch (loginErr) {
        if (isApiError(loginErr, "EMAIL_UNVERIFIED")) {
          navigate("/verify-email");
          return;
        }
        // BAD_CREDENTIALS after a 202 = the address already has an account
        // (the server mailed it a sign-in link). Honest, non-enumerating copy.
        if (isApiError(loginErr, "BAD_CREDENTIALS")) {
          navigate("/verify-email", { state: { existing: true } });
          return;
        }
      }
      navigate("/verify-email");
    } catch (err) {
      if (isApiError(err, "UNDERAGE")) {
        const ref = typeof err.details?.lock_ref === "string" ? err.details.lock_ref : "unavailable";
        writeUnderageLockRef(ref);
        setLockRef(ref);
      } else if (isApiError(err) && err.status === 429) {
        setError(resolveCopy("auth.throttled") ?? err.message);
      } else if (isApiError(err)) {
        setError(err.message);
      } else {
        setError("Network error — try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function onMagicLink() {
    if (!email) {
      setError("Enter your email first — the magic link needs a destination.");
      return;
    }
    setError(null);
    try {
      await api.post("/auth/magic-link", { body: { email } });
      setSignupAccount(email, null);
      setMagicSent(true);
    } catch (err) {
      if (isApiError(err) && err.status === 429) {
        setError(resolveCopy("auth.throttled") ?? err.message);
      } else {
        setMagicSent(true); // 202 semantics — membership never disclosed
      }
    }
  }

  async function onGoogle() {
    const started = await startGoogleSignIn();
    if (!started) toast(resolveCopy("auth.oauth_fail") ?? "Google sign-in unavailable.", "error");
  }

  return (
    <div className="auth auth--center" data-screen="S-04">
      <div className="auth__brand">
        <IconBrokenHeart width={24} height={24} style={{ color: "var(--accent)" }} />
        humble
      </div>
      <Card padding="lg">
        <h1 className="auth__title">Create your account</h1>
        {magicSent ? (
          <p role="status">{copy.auth.verify_wait}</p>
        ) : (
          <form onSubmit={onSubmit}>
            <div className="field">
              <label htmlFor="signup-email">Email</label>
              <input
                id="signup-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {disposable && (
              <p className="text-faint" role="note" style={{ fontSize: "0.85rem" }}>
                That looks like a throwaway inbox. Fine by us — but the verification mail and
                account recovery land there. Choose wisely.
              </p>
            )}
            <div className="field">
              <label htmlFor="signup-password">Password</label>
              <input
                id="signup-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={10}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="strength" aria-live="polite">
              <div className="strength__bar" aria-hidden="true">
                {[1, 2, 3, 4].map((seg) => (
                  <span
                    key={seg}
                    className={`strength__seg${strength.score >= seg ? ` strength__seg--on-${strength.score}` : ""}`}
                  />
                ))}
              </div>
              <span className="strength__label text-dim">
                {password ? `Password strength: ${strength.label}` : "10+ characters."}
              </span>
            </div>
            {error && (
              <p className="field__error" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" block variant="primary" size="lg" loading={busy}>
              Create account
            </Button>
            <div className="auth__row" style={{ marginTop: 8 }}>
              <Button block variant="ghost" onClick={() => void onMagicLink()}>
                Email me a link
              </Button>
              <Button block variant="ghost" onClick={() => void onGoogle()}>
                Continue with Google
              </Button>
            </div>
          </form>
        )}
      </Card>
      <div className="auth__footer">
        <Link to="/">Already have an account? Sign in</Link>
        <Link to="/legal/terms">Terms</Link>
        <Link to="/legal/privacy">Privacy</Link>
      </div>
    </div>
  );
}
